import React from 'react';
import { email, FormDataConsumer, Link, regex } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Typography, Grid, Box } from '@mui/material';

import { Theme } from 'src/Theme';
import { ChildFormFields, AdditionalEducationActionList, EducationAction, URL_REGEX } from 'src/constants';
import { ShsTextInput, ShsPhoneInput, ShsCheckbox, ShsRadioButtonGroup } from 'src/components/common';

const EDBO_URL = 'https://registry.edbo.gov.ua/schools/';
const INVALID_URL_MESSAGE =
    'Поле повинно відповідати формату URL адреси. Наприклад: "https://registry.edbo.gov.ua/schools/1259"';

const EducationInstitutionData: React.FC = () => {
    const formContext = useFormContext();
    return (
        <>
            <Typography
                variant="h5"
                component="h5"
                mb={3}
                mt={2}
                fontWeight="600"
                color={Theme.palette.primary.midnightBlue}>
                Відомості про освіту дитини за місцем проживання
            </Typography>
            <Grid container columnSpacing={4} rowSpacing={1}>
                <Grid item xs={12}>
                    <ShsTextInput
                        source={`${ChildFormFields.VISIT}.${ChildFormFields.EDUCATIONAL_INSTITUTION_REFERENCE}`}
                        label="Заклад освіти за місцем проживання"
                        placeholder="Посилання на заклад освіти в edbo"
                        validate={(value) => {
                            if (!value) return undefined;
                            return regex(URL_REGEX, INVALID_URL_MESSAGE)(value);
                        }}
                        helperText={
                            <Link
                                to={EDBO_URL}
                                target="_blank"
                                rel="noreferrer"
                                underline="always"
                                sx={{ fontSize: '16px' }}>
                                {EDBO_URL}
                            </Link>
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <ShsPhoneInput
                        source={`${ChildFormFields.VISIT}.${ChildFormFields.EDUCATIONAL_INSTITUTION_PHONE}`}
                        label="Номер телефону закладу освіти"
                    />
                </Grid>
                <Grid item xs={6}>
                    <ShsTextInput
                        source={`${ChildFormFields.VISIT}.${ChildFormFields.EDUCATIONAL_INSTITUTION_EMAIL}`}
                        label="Email закладу освіти"
                        validate={email()}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ShsCheckbox
                        source={`${ChildFormFields.VISIT}.${ChildFormFields.EDUCATIONAL_INSTITUTION_ADDITIONAL_ACTION}.enabled`}
                        label="Потрібна організація освітнього процесу в закладі загальної середньої освіти учнем/ученицею якого вони є"
                        onChange={(value: boolean) => {
                            if (!value) {
                                formContext.setValue('visit.additionalActionRequired.action', null);
                            }
                        }}
                        sx={{ mb: 4, color: Theme.palette.primary.midnightBlue }}
                    />
                    <FormDataConsumer>
                        {({ formData }) => {
                            const additionalAction: { enabled: boolean; action?: EducationAction } =
                                formData[ChildFormFields.VISIT]?.[
                                    ChildFormFields.EDUCATIONAL_INSTITUTION_ADDITIONAL_ACTION
                                ];

                            if (additionalAction?.enabled && !additionalAction?.action) {
                                additionalAction.action = AdditionalEducationActionList[0].id;
                            }
                            return (
                                additionalAction?.enabled && (
                                    <Box sx={{ mt: -2, ml: 2 }}>
                                        <Typography
                                            component="span"
                                            fontWeight="400"
                                            color={Theme.palette.primary.optional}>
                                            За формою:
                                        </Typography>
                                        <ShsRadioButtonGroup
                                            sx={{ color: Theme.palette.primary.midnightBlue }}
                                            source={`${ChildFormFields.VISIT}.${ChildFormFields.EDUCATIONAL_INSTITUTION_ADDITIONAL_ACTION}.action`}
                                            choices={AdditionalEducationActionList}
                                            label=""
                                            row={false}
                                            required
                                        />
                                    </Box>
                                )
                            );
                        }}
                    </FormDataConsumer>
                </Grid>
            </Grid>
        </>
    );
};

export default EducationInstitutionData;
